import { motion } from "framer-motion";
import {
  RadioTower,
  Radar as RadarIcon,
  PlaneLanding,
  Wrench,
  CalendarRange,
  Moon,
} from "lucide-react";

const modules = [
  {
    id: "tower",
    code: "T-01",
    icon: RadioTower,
    name: "Tower",
    tagline: "The family weather report.",
    body: "A calm dashboard that shows household status at a glance — stable, compressed, or overloaded — and where to look next.",
  },
  {
    id: "radar",
    code: "R-02",
    icon: RadarIcon,
    name: "Radar",
    tagline: "See it before it lands.",
    body: "Environmental scanning for approaching events, obligations, and pressures — so nothing arrives as a surprise.",
  },
  {
    id: "runway",
    code: "R-03",
    icon: PlaneLanding,
    name: "Runway",
    tagline: "Can this fit?",
    body: "Assess a new commitment against real capacity. Green, amber, red — with mitigations, not moralizing.",
  },
  {
    id: "hangar",
    code: "H-04",
    icon: Wrench,
    name: "Hangar",
    tagline: "The resources you actually have.",
    body: "People, time, money, support networks — inventoried in one place so you can see what's holding the household aloft.",
  },
  {
    id: "planner",
    code: "P-05",
    icon: CalendarRange,
    name: "Planner",
    tagline: "Landing impact, not just blocks.",
    body: "Schedule commitments and see their true cost — the setup, the aftermath, the compression they add to the week.",
  },
  {
    id: "nest",
    code: "N-06",
    icon: Moon,
    name: "Nest",
    tagline: "Protected room to breathe.",
    body: "A defended space for rest and restoration. Recovery time is treated as a resource, not a leftover.",
  },
];

export default function Modules() {
  return (
    <section
      id="modules"
      data-testid="modules-section"
      className="relative border-t border-rule py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="max-w-3xl">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-teal">
            02 — The instruments
          </p>
          <h2 className="mt-6 font-display text-4xl leading-[1.05] text-ink md:text-5xl">
            Six instruments. One tower.
          </h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-mist">
            Life OS borrows from aviation because pilots don&apos;t run on
            willpower. They run on instruments — calm, honest, forward-looking.
            Your household deserves the same.
          </p>
        </div>

        <div className="mt-16 grid gap-px border border-rule bg-rule md:grid-cols-2 lg:grid-cols-3">
          {modules.map((m, i) => {
            const Icon = m.icon;
            return (
              <motion.div
                key={m.id}
                data-testid={`module-card-${m.id}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.7,
                  delay: (i % 3) * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="group relative bg-obsidian p-8 transition-colors duration-500 hover:bg-deep"
              >
                {/* index */}
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                    {m.code}
                  </span>
                  <Icon
                    className="h-5 w-5 text-teal transition-colors duration-500 group-hover:text-amber"
                    strokeWidth={1.5}
                  />
                </div>

                <h3 className="mt-8 font-display text-3xl text-ink">
                  {m.name}
                </h3>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-teal">
                  {m.tagline}
                </p>
                <p className="mt-5 text-sm leading-relaxed text-mist">
                  {m.body}
                </p>

                {/* hover corner */}
                <span className="absolute bottom-3 right-3 h-2 w-2 border-b border-r border-teal opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
